
import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MobileBottomNav from './components/MobileBottomNav';
import Home from './pages/Home';
import { ArticleProvider } from './context/ArticleContext';
import { SiteProvider } from './context/SiteContext';
import { LinksProvider } from './context/LinksContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider } from './context/AuthContext';
import { AudioPlayerProvider } from './context/AudioPlayerContext';
import { GlobalAudioPlayer } from './components/GlobalAudioPlayer';
import AuthModal from './components/AuthModal';
import PublicationNotificationToast from './components/PublicationNotificationToast';
import PublicationNotificationWatcher from './components/PublicationNotificationWatcher';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsentBanner from './components/CookieConsentBanner';
import { OfflineIndicator } from './components/OfflineIndicator';
import WebPushBanner from './components/WebPushBanner';
import { startIdleBackgroundPreload } from './utils/imagePreloader';

// Lazy-loaded pages (carregados sob demanda apenas quando acessados, reduzindo o bundle inicial do primeiro acesso)
const Articles = lazy(() => import('./pages/Articles'));
const Community = lazy(() => import('./pages/Community'));
const Bookstore = lazy(() => import('./pages/Bookstore'));
const DynamicPageRenderer = lazy(() => import('./pages/DynamicPageRenderer'));
const LinksPage = lazy(() => import('./pages/LinksPage'));
const Profile = lazy(() => import('./pages/Profile'));
const ArticleView = lazy(() => import('./pages/ArticleView'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));


// Redireciona links antigos com hash (#/artigo/...) para URLs limpas (/artigo/...)
const HashRedirectHandler: React.FC = () => {
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const hash = window.location.hash;
      if (hash.startsWith('#/')) {
        const cleanPath = hash.slice(1);
        navigate(cleanPath, { replace: true });
      }
    }
  }, [navigate]);
  return null;
};

// Componente elegante de carregamento sob demanda
const PageLoadingFallback: React.FC<{ message?: string }> = ({ message = 'Carregando...' }) => (
  <div className="min-h-[70vh] flex flex-col items-center justify-center bg-paper text-stone-300 font-sans px-4">
    <div className="relative flex items-center justify-center mb-4">
      <div className="w-12 h-12 rounded-full border-2 border-stone-800 border-t-primary animate-spin"></div>
      <div className="absolute w-6 h-6 rounded-full bg-primary/20 blur-sm animate-pulse"></div>
    </div>
    <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{message}</p>
  </div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Layout wrapper for standard pages (with Navbar/Footer)
const StandardLayout: React.FC<{children: React.ReactNode, noFooter?: boolean}> = ({ children, noFooter }) => (
  <div className="flex flex-col min-h-screen font-sans bg-paper text-stone-200 pb-[calc(7rem+env(safe-area-inset-bottom))] md:pb-0">
    <Navbar />
    <main className="flex-grow">
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    </main>
    {!noFooter && <Footer />}
    <MobileBottomNav />
  </div>
);

const App: React.FC = () => {
  useEffect(() => {
    // Adia tarefas de background não críticas para depois que a thread principal estiver livre (melhor TBT/LCP no mobile)
    const runDeferred = () => {
      startIdleBackgroundPreload();
      import('./services/webPushService').then(({ autoSyncWebPushSubscription }) => {
        autoSyncWebPushSubscription().catch(() => {});
      }).catch(() => {});
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      (window as any).requestIdleCallback(runDeferred, { timeout: 4000 });
    } else {
      setTimeout(runDeferred, 2500);
    }
  }, []);

  return (
    <LanguageProvider>
      <LinksProvider>
        <SiteProvider>
          <ArticleProvider>
            <AuthProvider>
              <AudioPlayerProvider>
                <BrowserRouter>
                  <HashRedirectHandler />
                  <ScrollToTop />
                  <Routes>
                    {/* Public Routes with Navbar/Footer */}
                    <Route path="/" element={<StandardLayout noFooter><Home /></StandardLayout>} />
                    <Route 
                      path="/artigos" 
                      element={
                        <StandardLayout>
                          <Suspense fallback={<PageLoadingFallback message="Carregando artigos..." />}>
                            <Articles />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    <Route 
                      path="/comunidade" 
                      element={
                        <StandardLayout noFooter>
                          <Suspense fallback={<PageLoadingFallback message="Carregando comunidade..." />}>
                            <Community />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    <Route 
                      path="/comunidade/booklists/:bookListId" 
                      element={
                        <StandardLayout noFooter>
                          <Suspense fallback={<PageLoadingFallback message="Carregando comunidade..." />}>
                            <Community />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    
                    {/* Artigo completo carregado sob demanda apenas ao clicar */}
                    <Route 
                      path="/artigo/:id" 
                      element={
                        <StandardLayout>
                          <Suspense fallback={<PageLoadingFallback message="Abrindo artigo..." />}>
                            <ArticleView />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    {/* Suporte à rota plural /artigos/:id para URLs digitadas ou indexadas */}
                    <Route 
                      path="/artigos/:id" 
                      element={
                        <StandardLayout>
                          <Suspense fallback={<PageLoadingFallback message="Abrindo artigo..." />}>
                            <ArticleView />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />

                    <Route 
                      path="/livraria" 
                      element={
                        <StandardLayout noFooter>
                          <Suspense fallback={<PageLoadingFallback message="Carregando livraria..." />}>
                            <Bookstore />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    <Route 
                      path="/p/:path" 
                      element={
                        <StandardLayout>
                          <Suspense fallback={<PageLoadingFallback message="Carregando página..." />}>
                            <DynamicPageRenderer />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    <Route 
                      path="/perfil" 
                      element={
                        <StandardLayout>
                          <Suspense fallback={<PageLoadingFallback message="Carregando perfil..." />}>
                            <Profile />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />

                    {/* Dedicated Monetization & Legal Routes (Acessíveis para indexação e AdSense) */}
                    <Route 
                      path="/privacidade" 
                      element={
                        <StandardLayout>
                          <Suspense fallback={<PageLoadingFallback message="Carregando política de privacidade..." />}>
                            <PrivacyPolicy />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    <Route path="/politica-de-privacidade" element={<Navigate to="/privacidade" replace />} />

                    <Route 
                      path="/termos" 
                      element={
                        <StandardLayout>
                          <Suspense fallback={<PageLoadingFallback message="Carregando termos de uso..." />}>
                            <TermsOfService />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    <Route path="/termos-de-uso" element={<Navigate to="/termos" replace />} />

                    <Route 
                      path="/sobre" 
                      element={
                        <StandardLayout>
                          <Suspense fallback={<PageLoadingFallback message="Carregando sobre nós..." />}>
                            <About />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    <Route path="/quem-somos" element={<Navigate to="/sobre" replace />} />

                    <Route 
                      path="/contato" 
                      element={
                        <StandardLayout>
                          <Suspense fallback={<PageLoadingFallback message="Carregando fale conosco..." />}>
                            <Contact />
                          </Suspense>
                        </StandardLayout>
                      } 
                    />
                    <Route path="/fale-conosco" element={<Navigate to="/contato" replace />} />
                    
                    {/* Hidden Links Aggregator Page */}
                    <Route 
                      path="/links" 
                      element={
                        <Suspense fallback={<PageLoadingFallback message="Carregando links..." />}>
                          <LinksPage />
                        </Suspense>
                      } 
                    />
                    
                    {/* Admin Routes (Carregados sob demanda, isolados do usuário comum) */}
                    <Route 
                      path="/admin" 
                      element={
                        <Suspense fallback={<PageLoadingFallback message="Carregando portal administrativo..." />}>
                          <AdminLogin />
                        </Suspense>
                      } 
                    />
                    <Route 
                      path="/admin/dashboard" 
                      element={
                        <Suspense fallback={<PageLoadingFallback message="Acessando painel de controle..." />}>
                          <AdminDashboard />
                        </Suspense>
                      } 
                    />
                    
                    {/* Catch-all route to redirect to home if page not found */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  <GlobalAudioPlayer />
                  <AuthModal />
                  <PublicationNotificationToast />
                  <PublicationNotificationWatcher />
                  <CookieConsentBanner />
                  <OfflineIndicator />
                  <WebPushBanner />
                </BrowserRouter>
              </AudioPlayerProvider>
            </AuthProvider>
          </ArticleProvider>
        </SiteProvider>
      </LinksProvider>
    </LanguageProvider>
  );
};

export default App;
